import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Link as MuiLink,
  Stack,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  People,
  Schedule,
  SportsTennis,
  ArrowBack,
  Email,
  Security,
  Star,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import { Tournament, Match } from '../types';
import { getAvatarUrl, isDefaultAvatar } from '../utils/avatar';
import {
  avatarInitials,
  playerAvatarSx,
  AVATAR_STACK_CUTOUT,
  teamAvatarStackWidth,
} from '../utils/playerAvatarDisplay';
import {
  PlayerProfileModal,
  PlayerProfileTarget,
  playerSlotTarget,
} from '../components/PlayerProfileModal';
import { formatMatchScoreDisplay } from '../utils/scoreForm';

const FORMAT_LABELS: Record<string, string> = {
  round_robin: 'Round Robin',
  single_elimination: 'Single Elimination',
  double_elimination: 'Double Elimination',
  swiss_system: 'Swiss System',
  league: 'League',
  flex_league: 'Flex League',
  montante_descendante: 'Montante/Descendante',
  americano: 'Americano',
  mexicano: 'Mexicano',
  solo_free_play: 'Solo Free Play',
};

function getFormatLabel(format: string, t: (key: string) => string): string {
  const key = `tournamentPublic.formats.${format}`;
  const translated = t(key);
  return translated !== key ? translated : FORMAT_LABELS[format] || format;
}

function getDateLocale(language: string | undefined): string {
  return language?.startsWith('fr') ? 'fr-FR' : 'en-US';
}

const TEAM_BASED_TOURNAMENT_FORMATS = [
  'league',
  'round_robin',
  'single_elimination',
  'double_elimination',
  'swiss_system',
] as const;

function normalizeTournamentFormat(format: string | undefined | null): string {
  const raw = (format || 'round_robin').toString().toLowerCase();
  return raw === 'montantedescendante' ? 'montante_descendante' : raw;
}

function isTeamBasedTournamentFormat(format: string | undefined | null): boolean {
  return (TEAM_BASED_TOURNAMENT_FORMATS as readonly string[]).includes(normalizeTournamentFormat(format));
}

const TOURNAMENT_STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  active: { bg: 'rgba(34, 197, 94, 0.25)', color: '#22c55e' },
  registration: { bg: 'rgba(59, 130, 246, 0.25)', color: '#3b82f6' },
  upcoming: { bg: 'rgba(168, 85, 247, 0.25)', color: '#a855f7' },
  completed: { bg: 'rgba(107, 114, 128, 0.25)', color: '#9ca3af' },
  cancelled: { bg: 'rgba(239, 68, 68, 0.25)', color: '#ef4444' },
};

const MATCH_STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  completed: { bg: 'rgba(34, 197, 94, 0.25)', color: '#22c55e' },
  confirmed: { bg: 'rgba(59, 130, 246, 0.25)', color: '#3b82f6' },
  scheduled: { bg: 'rgba(168, 85, 247, 0.25)', color: '#a855f7' },
  in_progress: { bg: 'rgba(234, 179, 8, 0.25)', color: '#eab308' },
  to_be_scheduled: { bg: 'rgba(107, 114, 128, 0.25)', color: '#9ca3af' },
  pending: { bg: 'rgba(107, 114, 128, 0.25)', color: '#9ca3af' },
  cancelled: { bg: 'rgba(239, 68, 68, 0.25)', color: '#ef4444' },
  rejected: { bg: 'rgba(239, 68, 68, 0.25)', color: '#ef4444' },
};

function getTournamentStatusStyle(status: string) {
  const s = (status || '').toLowerCase();
  return TOURNAMENT_STATUS_COLORS[s] || { bg: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.9)' };
}

function getMatchStatusStyle(status: string) {
  const s = (status || 'pending').toLowerCase().replace(/-/g, '_');
  return MATCH_STATUS_COLORS[s] || { bg: 'rgba(107, 114, 128, 0.25)', color: '#9ca3af' };
}

function matchToRowEntry(
  match: any,
  side: 'team1' | 'team2',
  participantById: Map<number, any>,
  tbdLabel: string
) {
  const teamId = side === 'team1' ? match.team1_id : match.team2_id;
  const participant = teamId && teamId > 0 ? participantById.get(teamId) : null;
  if (participant) {
    return participantToRowEntry(participant);
  }
  const prefix = side;
  return {
    team_name: match[`${prefix}_name`] || null,
    player1_id: match[`${prefix}_player1_id`] ?? null,
    player2_id: match[`${prefix}_player2_id`] ?? null,
    player1_name: match[`${prefix}_player1_name`] || match[`${prefix}_name`] || tbdLabel,
    player2_name: match[`${prefix}_player2_name`] ?? null,
    player1_avatar_url: match[`${prefix}_player1_avatar_url`] ?? null,
    player2_avatar_url: match[`${prefix}_player2_avatar_url`] ?? null,
    player1_pending_invite: !match[`${prefix}_player1_id`] && !!match[`${prefix}_player1_name`],
    player2_pending_invite: !match[`${prefix}_player2_id`] && !!match[`${prefix}_player2_name`],
  };
}

function getTranslatedTournamentStatus(status: string, t: (key: string) => string) {
  const key = `tournamentPublic.statusTournament.${(status || '').toLowerCase()}`;
  const translated = t(key);
  return translated !== key ? translated : status;
}

function getTranslatedMatchStatus(status: string, t: (key: string) => string) {
  const s = (status || 'pending').toLowerCase().replace(/-/g, '_');
  const key = `tournamentPublic.statusMatch.${s}`;
  const translated = t(key);
  return translated !== key ? translated : status;
}

function renderPlayerAvatar(
  avatarUrl: string | null | undefined,
  name?: string | null,
  size = 32,
  onClick?: () => void,
  slotIndex = 0
) {
  const hasPhoto = !isDefaultAvatar(avatarUrl) && !!getAvatarUrl(avatarUrl);
  const src = hasPhoto ? getAvatarUrl(avatarUrl) : undefined;
  const avatar = (
    <Avatar src={src} sx={playerAvatarSx(name, size, hasPhoto, slotIndex)}>
      {!hasPhoto ? avatarInitials(name) : null}
    </Avatar>
  );
  if (!onClick) return avatar;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={name || undefined}
      sx={{
        border: 'none',
        background: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'inline-flex',
        borderRadius: '50%',
        transition: 'transform 0.15s ease, filter 0.15s ease',
        '&:hover': { transform: 'scale(1.06)', filter: 'brightness(1.08)' },
        '&:focus-visible': {
          outline: `2px solid ${AVATAR_STACK_CUTOUT}`,
          outlineOffset: 2,
        },
      }}
    >
      {avatar}
    </Box>
  );
}

function renderTeamAvatarStack(
  entry: {
    player1_name?: string | null;
    player2_name?: string | null;
    player1_avatar_url?: string | null;
    player2_avatar_url?: string | null;
  },
  size: number,
  onOpenPlayer1?: () => void,
  onOpenPlayer2?: () => void,
  showSecond = true
) {
  const stackWidth = showSecond && entry.player2_name
    ? teamAvatarStackWidth(size)
    : size;

  return (
    <Box
      sx={{
        position: 'relative',
        width: stackWidth,
        height: size,
        flexShrink: 0,
      }}
    >
      {showSecond && entry.player2_name ? (
        <Box
          sx={{
            position: 'absolute',
            left: Math.round(size * 0.55),
            top: 0,
            zIndex: 1,
          }}
        >
          {renderPlayerAvatar(
            entry.player2_avatar_url,
            entry.player2_name,
            size,
            onOpenPlayer2,
            1
          )}
        </Box>
      ) : null}
      <Box sx={{ position: 'absolute', left: 0, top: 0, zIndex: 2 }}>
        {renderPlayerAvatar(
          entry.player1_avatar_url,
          entry.player1_name,
          size,
          onOpenPlayer1,
          0
        )}
      </Box>
    </Box>
  );
}

function renderClickablePlayerName(
  name: string,
  onClick: () => void,
  sx?: object
) {
  return (
    <Typography
      component="button"
      type="button"
      variant="body2"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        border: 'none',
        background: 'none',
        padding: 0,
        cursor: 'pointer',
        textAlign: 'left',
        color: 'rgba(255,255,255,0.9)',
        font: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        lineHeight: 'inherit',
        '&:hover': { color: 'primary.main', textDecoration: 'underline' },
        ...sx,
      }}
    >
      {name}
    </Typography>
  );
}

function participantToRowEntry(p: any) {
  return {
    team_name: p.team_name,
    player1_id: p.player1_id,
    player2_id: p.player2_id,
    player1_name: p.player1_name,
    player2_name: p.player2_name,
    player1_avatar_url: p.player1_avatar_url,
    player2_avatar_url: p.player2_avatar_url,
    player1_pending_invite: !p.player1_id && !!p.player1_name,
    player2_pending_invite: !p.player2_id && !!p.player2_name,
  };
}

function renderTeamRankCell(
  entry: {
    team_name?: string | null;
    player1_id?: number | null;
    player2_id?: number | null;
    player1_name?: string | null;
    player2_name?: string | null;
    player1_avatar_url?: string | null;
    player2_avatar_url?: string | null;
    player1_pending_invite?: boolean;
    player2_pending_invite?: boolean;
  },
  showAsTeam: boolean,
  t: (key: string) => string,
  onOpenPlayer: (target: PlayerProfileTarget) => void,
  layout: 'ranking' | 'teams' = 'ranking'
) {
  const unknownLabel = t('tournamentPublic.unknown');
  const composition = [entry.player1_name, entry.player2_name].filter(Boolean).join(' & ');
  const customTeamLabel =
    entry.team_name && entry.team_name.trim() !== composition ? entry.team_name.trim() : null;
  const displayTeamName = entry.team_name?.trim() || composition || unknownLabel;

  const slot1 = playerSlotTarget(
    entry.player1_id,
    entry.player1_name,
    entry.player1_avatar_url,
    entry.player1_pending_invite ?? !entry.player1_id
  );
  const slot2 = playerSlotTarget(
    entry.player2_id,
    entry.player2_name,
    entry.player2_avatar_url,
    entry.player2_pending_invite ?? !entry.player2_id
  );

  const openSlot = (slot: PlayerProfileTarget | null) => {
    if (slot) onOpenPlayer(slot);
  };

  const renderPlayerLine = (emphasis = false, subtle = false) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
      {slot1 ? renderClickablePlayerName(entry.player1_name || unknownLabel, () => openSlot(slot1), emphasis ? {
        color: 'rgba(255,255,255,0.95)',
        fontWeight: 600,
      } : subtle ? {
        color: 'rgba(255,255,255,0.55)',
        fontSize: '0.8125rem',
      } : undefined) : entry.player1_name ? (
        <Typography variant="body2" sx={{ color: subtle ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.9)', fontSize: subtle ? '0.8125rem' : undefined }}>{entry.player1_name}</Typography>
      ) : null}
      {slot1 && slot2 ? (
        <Typography variant="body2" sx={{ color: subtle ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.55)', fontSize: subtle ? '0.8125rem' : undefined }}>&</Typography>
      ) : null}
      {slot2 ? renderClickablePlayerName(entry.player2_name || unknownLabel, () => openSlot(slot2), emphasis ? {
        color: 'rgba(255,255,255,0.95)',
        fontWeight: 600,
      } : subtle ? {
        color: 'rgba(255,255,255,0.55)',
        fontSize: '0.8125rem',
      } : undefined) : entry.player2_name ? (
        <Typography variant="body2" sx={{ color: subtle ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.9)', fontSize: subtle ? '0.8125rem' : undefined }}>{entry.player2_name}</Typography>
      ) : null}
    </Box>
  );

  if (!showAsTeam) {
    const label = entry.player1_name || unknownLabel;
    const avatarSize = layout === 'teams' ? 36 : 32;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {renderPlayerAvatar(entry.player1_avatar_url, entry.player1_name, avatarSize, slot1 ? () => openSlot(slot1) : undefined, 0)}
        {layout === 'teams' ? (
          slot1 ? renderClickablePlayerName(label, () => openSlot(slot1), {
            fontWeight: 700,
            fontSize: '1.05rem',
            color: 'rgba(255,255,255,0.95)',
          }) : (
            <Typography variant="body1" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.95)', fontSize: '1.05rem' }}>
              {label}
            </Typography>
          )
        ) : slot1 ? renderClickablePlayerName(label, () => openSlot(slot1), { fontWeight: 600 }) : (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>{label}</Typography>
        )}
      </Box>
    );
  }

  if (layout === 'teams') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.75 }, minWidth: 0, maxWidth: '100%' }}>
        {renderTeamAvatarStack(
          entry,
          36,
          slot1 ? () => openSlot(slot1) : undefined,
          slot2 ? () => openSlot(slot2) : undefined,
          !!entry.player2_name
        )}
        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              color: 'rgba(255,255,255,0.95)',
              fontSize: { xs: '0.9375rem', sm: '1.05rem' },
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {displayTeamName}
          </Typography>
          {customTeamLabel && (entry.player1_name || entry.player2_name) ? (
            <Box sx={{ mt: 0.35 }}>{renderPlayerLine(false, true)}</Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.75 }, minWidth: 0, maxWidth: '100%' }}>
      {renderTeamAvatarStack(
        entry,
        32,
        slot1 ? () => openSlot(slot1) : undefined,
        slot2 ? () => openSlot(slot2) : undefined,
        !!(entry.player2_name && showAsTeam)
      )}
      <Box>
        {renderPlayerLine(true)}
        {customTeamLabel ? (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.25 }}>
            {customTeamLabel}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

const featureCardStyle = {
  p: { xs: 2.5, sm: 3 },
  borderRadius: { xs: '1rem', sm: '1.5rem' },
  bgcolor: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.05)',
  transition: 'background-color 0.2s',
  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.06)' },
  backdropFilter: 'blur(8px)',
  maxWidth: '100%',
  overflow: 'hidden',
};

export const TournamentPublicPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? parseInt(id, 10) : null;
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [ranking, setRanking] = useState<any[]>([]);
  const [bracket, setBracket] = useState<any>(null);
  const [ladder, setLadder] = useState<any>(null);
  const [flexLeaderboard, setFlexLeaderboard] = useState<any[]>([]);
  const [participants, setParticipants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [profileTarget, setProfileTarget] = useState<PlayerProfileTarget | null>(null);

  const profileByUserId = React.useMemo(() => {
    const map = new Map<number, NonNullable<Extract<PlayerProfileTarget, { kind: 'registered' }>['prefetchedProfile']>>();
    participants.forEach((p: any) => {
      if (p.player1_id && p.player1_public_profile) {
        map.set(p.player1_id, p.player1_public_profile);
      }
      if (p.player2_id && p.player2_public_profile) {
        map.set(p.player2_id, p.player2_public_profile);
      }
    });
    return map;
  }, [participants]);

  const openPlayerProfile = (target: PlayerProfileTarget) => {
    if (target.kind !== 'registered') {
      setProfileTarget(target);
      return;
    }
    const prefetchedProfile = profileByUserId.get(target.userId);
    setProfileTarget(
      prefetchedProfile ? { ...target, prefetchedProfile } : target
    );
  };
  const closePlayerProfile = () => setProfileTarget(null);
  useEffect(() => {
    if (!tournamentId || isNaN(tournamentId)) {
      setError(t('tournamentPublic.invalidTournamentId'));
      setLoading(false);
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  const loadData = async () => {
    if (!tournamentId) return;
    try {
      setLoading(true);
      setError('');
      const [tournamentRes, matchesRes, participantsRes] = await Promise.all([
        apiService.getTournament(tournamentId),
        apiService.getTournamentMatches(tournamentId),
        apiService.getTournamentParticipants(tournamentId),
      ]);
      setTournament(tournamentRes.data);
      const participantsData = participantsRes.data as any;
      setParticipants(Array.isArray(participantsData) ? participantsData : []);
      const matchesData = matchesRes.data as any;
      setMatches(Array.isArray(matchesData) ? matchesData : matchesData?.data || []);

      const fmt = (tournamentRes.data?.format || 'round_robin').toString().toLowerCase();
      if (fmt === 'single_elimination' || fmt === 'double_elimination') {
        try {
          const bracketRes = await apiService.getTournamentBracket(tournamentId);
          setBracket(bracketRes.data);
        } catch {
          setBracket(null);
        }
      } else if (fmt === 'montante_descendante') {
        try {
          const ladderRes = await apiService.getTournamentLadder(tournamentId);
          setLadder(ladderRes.data);
        } catch {
          setLadder(null);
        }
      } else if (fmt === 'flex_league') {
        try {
          const flexRes = await apiService.getFlexLeagueLeaderboard(tournamentId);
          setFlexLeaderboard(Array.isArray(flexRes.data) ? flexRes.data : []);
        } catch {
          setFlexLeaderboard([]);
        }
      } else {
        try {
          const rankRes = await apiService.getTournamentRanking(tournamentId);
          setRanking(Array.isArray(rankRes.data) ? rankRes.data : []);
        } catch {
          setRanking([]);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('tournamentPublic.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const format = normalizeTournamentFormat(tournament?.format?.toString());
  const isBracketFormat = format === 'single_elimination' || format === 'double_elimination';
  const isLadderFormat = format === 'montante_descendante';
  const isFlexFormat = format === 'flex_league';
  const isPointsFormat = format === 'mexicano' || format === 'americano';
  const isTeamBasedFormat = isTeamBasedTournamentFormat(format);
  const isRoundBasedFormat = isLadderFormat || isPointsFormat;
  const showMatchAsPairs = isPointsFormat || isLadderFormat;

  const matchesByRound = useMemo(() => {
    if (!isRoundBasedFormat || matches.length === 0) return null;
    const map = new Map<number, any[]>();
    for (const m of matches) {
      const round = m.round_number ?? 1;
      if (!map.has(round)) map.set(round, []);
      map.get(round)!.push(m);
    }
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [matches, isRoundBasedFormat]);

  const matchesByRoundAndCourt = useMemo(() => {
    if (!isPointsFormat || !matchesByRound) return null;
    return matchesByRound.map(([round, roundMatches]) => {
      const courtMap = new Map<string, any[]>();
      for (const m of roundMatches) {
        const label =
          m.court_name ||
          (m.court_id ? `${t('tournamentPublic.court')} ${m.court_id}` : t('tournamentPublic.courtTbd'));
        if (!courtMap.has(label)) courtMap.set(label, []);
        courtMap.get(label)!.push(m);
      }
      return { round, courts: Array.from(courtMap.entries()) };
    });
  }, [matchesByRound, isPointsFormat, t]);

  const latestRoundCourts = matchesByRoundAndCourt?.length
    ? matchesByRoundAndCourt[0]
    : null;
  const dateLocale = getDateLocale(i18n.language);
  const tbdLabel = t('tournamentPublic.tbd');
  const vsLabel = t('tournamentPublic.vs');

  const participantById = React.useMemo(() => {
    const map = new Map<number, any>();
    participants.forEach((p) => map.set(p.id, p));
    return map;
  }, [participants]);

  const approvedParticipants = React.useMemo(
    () => participants.filter((p) => !p.status || p.status === 'approved'),
    [participants]
  );

  const renderParticipantCell = (participantId: number, fallbackName: string) => {
    const p = participantById.get(participantId);
    if (!p) {
      return (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>{fallbackName}</Typography>
      );
    }
    return renderTeamRankCell(
      participantToRowEntry(p),
      isTeamBasedFormat,
      t,
      openPlayerProfile
    );
  };

  const enrichRankingRow = (r: any) => {
    const p = participantById.get(r.participant_id);
    if (!p) return r;
    return {
      ...r,
      team_name: r.team_name ?? p.team_name,
      player1_id: r.player1_id ?? p.player1_id,
      player2_id: r.player2_id ?? p.player2_id,
      player1_name: r.player1_name ?? p.player1_name,
      player2_name: r.player2_name ?? p.player2_name,
      player1_avatar_url: r.player1_avatar_url ?? p.player1_avatar_url,
      player2_avatar_url: r.player2_avatar_url ?? p.player2_avatar_url,
      player1_pending_invite: r.player1_pending_invite ?? (!p.player1_id && !!p.player1_name),
      player2_pending_invite: r.player2_pending_invite ?? (!p.player2_id && !!p.player2_name),
    };
  };

  const renderFlexPlayerCell = (entry: any) => {
    const p = participantById.get(entry.participant_id);
    const playerId = entry.player_id ?? p?.player1_id;
    const playerName = entry.player_name ?? p?.player1_name;
    const avatarUrl = entry.avatar_url ?? p?.player1_avatar_url;
    const pendingInvite = entry.pending_invite ?? (!playerId && !!playerName);
    const target = playerSlotTarget(playerId, playerName, avatarUrl, pendingInvite);
    if (!target) {
      return playerName || entry.player_name;
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {renderPlayerAvatar(avatarUrl, playerName, 32, () => openPlayerProfile(target), 0)}
        {renderClickablePlayerName(playerName || entry.player_name, () => openPlayerProfile(target))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#0a0a0a',
          color: 'white',
        }}
      >
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  if (error || !tournament) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Paper elevation={0} sx={{ ...featureCardStyle, maxWidth: 500, width: '100%' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || t('tournamentPublic.tournamentNotFound')}
          </Alert>
          <Button component={Link} to="/" startIcon={<ArrowBack />} variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
            {t('tournamentPublic.backToHome')}
          </Button>
        </Paper>
      </Box>
    );
  }

  const participantsCount =
    (tournament as any).participants_count ??
    (tournament as any).approved_participants_count ??
    (tournament as any).current_participants ??
    0;

  const tableCellStyle = { color: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(255, 255, 255, 0.08)' };
  const tableHeaderStyle = { ...tableCellStyle, fontWeight: 700, color: 'rgba(255, 255, 255, 0.7)' };

  const responsiveTableWrapper = {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as const,
    '& .MuiTable-root': {
      width: '100%',
      tableLayout: { xs: 'fixed', md: 'auto' },
    },
  };

  const compactTableCell = { display: { xs: 'none', sm: 'table-cell' } };

  const renderMobileMatchCard = (m: any) => {
    const scoreText = formatMatchScoreDisplay(m);
    const team1Entry = matchToRowEntry(m, 'team1', participantById, tbdLabel);
    const team2Entry = matchToRowEntry(m, 'team2', participantById, tbdLabel);
    const pairMode = showMatchAsPairs || isTeamBasedFormat;

    return (
      <Paper
        key={m.id}
        elevation={0}
        sx={{
          p: 1.5,
          bgcolor: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.05)',
          borderRadius: '1rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
            {m.match_date ? new Date(m.match_date).toLocaleDateString(dateLocale) : '-'}
          </Typography>
          {isPointsFormat && (m.court_name || m.court_id) ? (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
              {m.court_name || `${t('tournamentPublic.court')} ${m.court_id}`}
            </Typography>
          ) : null}
          <Chip
            label={getTranslatedMatchStatus(m.status || 'pending', t)}
            size="small"
            sx={{
              ml: { xs: 0, sm: 'auto' },
              bgcolor: getMatchStatusStyle(m.status).bg,
              color: getMatchStatusStyle(m.status).color,
              border: `1px solid ${getMatchStatusStyle(m.status).color}40`,
              fontWeight: 600,
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            {renderTeamRankCell(team1Entry, pairMode, t, openPlayerProfile, 'teams')}
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            {scoreText ? (
              <Typography fontWeight="bold" sx={{ color: 'white' }}>
                {scoreText}
              </Typography>
            ) : (
              <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>{vsLabel}</Typography>
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {renderTeamRankCell(team2Entry, pairMode, t, openPlayerProfile, 'teams')}
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        bgcolor: '#0a0a0a',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100vw',
        position: 'relative',
        color: 'white',
      }}
    >
      {/* Background – same as LandingPage */}
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-20%', left: '-10%', width: '50%', height: '50%', bgcolor: 'primary.main', opacity: 0.2, borderRadius: '50%', filter: 'blur(120px)' }} />
        <Box sx={{ position: 'absolute', bottom: '-20%', right: '-10%', width: '40%', height: '40%', bgcolor: '#2563eb', opacity: 0.1, borderRadius: '50%', filter: 'blur(100px)' }} />
      </Box>

      {/* Navigation – same as LandingPage */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 10,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 80 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'center' }}>
          <img src="/PadPal.png" alt="PadPal Logo" style={{ width: 32, height: 32, borderRadius: 8 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' }, letterSpacing: '-0.02em' }}>
            PadPal
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, minWidth: 80, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={() => i18n.changeLanguage('en')}
            sx={{
              minWidth: 36,
              color: i18n.language?.startsWith('en') ? 'primary.main' : 'rgba(255,255,255,0.6)',
              fontWeight: i18n.language?.startsWith('en') ? 700 : 400,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            EN
          </Button>
          <Button
            size="small"
            onClick={() => i18n.changeLanguage('fr')}
            sx={{
              minWidth: 36,
              color: i18n.language?.startsWith('fr') ? 'primary.main' : 'rgba(255,255,255,0.6)',
              fontWeight: i18n.language?.startsWith('fr') ? 700 : 400,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            FR
          </Button>
        </Box>
      </Box>

      {/* Hero Section – same structure as LandingPage */}
      <Box
        component="main"
        sx={{
          flex: 1,
          width: '100%',
          maxWidth: '1400px',
          minWidth: 0,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, sm: 6, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          zIndex: 10,
          overflowX: 'hidden',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{ width: '100%' }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem', lg: '4rem' },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              mb: { xs: 2, sm: 3 },
              color: 'white',
              lineHeight: 1.1,
            }}
          >
            {tournament.name}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.125rem' },
              color: 'rgba(255, 255, 255, 0.7)',
              mb: 2,
              lineHeight: 1.6,
            }}
          >
            <Schedule sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
            {new Date(tournament.start_date).toLocaleDateString(dateLocale)} – {new Date(tournament.end_date).toLocaleDateString(dateLocale)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 3 }}>
            <Chip
              label={getTranslatedTournamentStatus(tournament.status, t)}
              size="small"
              sx={{
                bgcolor: getTournamentStatusStyle(tournament.status).bg,
                color: getTournamentStatusStyle(tournament.status).color,
                border: `1px solid ${getTournamentStatusStyle(tournament.status).color}40`,
                fontWeight: 600,
              }}
            />
            <Chip
              label={getFormatLabel(format, t)}
              size="small"
              sx={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.9)' }}
              variant="outlined"
            />
            <Typography component="span" variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'inline-flex', alignItems: 'center' }}>
              <People sx={{ fontSize: 16, mr: 0.5 }} />
              {participantsCount}{' '}
              {t(isTeamBasedFormat ? 'tournamentPublic.teamsCount' : 'tournamentPublic.participants')}
            </Typography>
          </Box>
        </motion.div>

        {/* Content Cards – same style as LandingPage feature grid */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', sm: '48rem', md: '64rem' },
            minWidth: 0,
            mt: { xs: 4, sm: 6 },
            overflow: 'hidden',
          }}
        >
          {tournament.description && (
            <Paper elevation={0} sx={{ ...featureCardStyle, mb: 3, textAlign: 'left' }}>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.7 }}>
                {tournament.description}
              </Typography>
            </Paper>
          )}

          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              mb: 3,
              minHeight: { xs: 40, sm: 48 },
              '& .MuiTab-root': { color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.8125rem', sm: '0.875rem' }, minHeight: { xs: 40, sm: 48 }, px: { xs: 1.5, sm: 2 } },
              '& .Mui-selected': { color: 'white' },
              '& .MuiTabs-indicator': { bgcolor: 'primary.main' },
            }}
          >
            <Tab label={isBracketFormat && bracket ? t('tournamentPublic.bracket') : isLadderFormat ? t('tournamentPublic.ladder') : isPointsFormat ? t('tournamentPublic.standings') : t('tournamentPublic.rankings')} />
            <Tab label={t(isTeamBasedFormat ? 'tournamentPublic.teamsTab' : 'tournamentPublic.playersTab')} />
            <Tab label={t('tournamentPublic.matches')} />
          </Tabs>

          {tabValue === 0 && isBracketFormat && bracket && (
            <Paper elevation={0} sx={{ ...featureCardStyle, mb: 3, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
                {t('tournamentPublic.bracket')}
              </Typography>
              {bracket.rounds?.map((r: any) => (
                <Box key={r.round} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
                    {t('tournamentPublic.round')} {r.round}
                  </Typography>
                  {r.matches?.map((m: any) => {
                    const scoreText = formatMatchScoreDisplay(m);
                    const team1Entry = matchToRowEntry(m, 'team1', participantById, tbdLabel);
                    const team2Entry = matchToRowEntry(m, 'team2', participantById, tbdLabel);
                    return (
                    <Paper
                      key={m.match_id}
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        mb: 1,
                        bgcolor: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.05)',
                        borderRadius: '1rem',
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        gap={2}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {renderTeamRankCell(team1Entry, isTeamBasedFormat, t, openPlayerProfile, 'teams')}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, px: { md: 1 }, flexShrink: 0 }}>
                          {scoreText ? (
                            <Typography fontWeight="bold" sx={{ color: 'white', textAlign: 'center' }}>{scoreText}</Typography>
                          ) : (
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>{vsLabel}</Typography>
                          )}
                          {m.status && (
                            <Chip
                              label={getTranslatedMatchStatus(m.status, t)}
                              size="small"
                              sx={{
                                bgcolor: getMatchStatusStyle(m.status).bg,
                                color: getMatchStatusStyle(m.status).color,
                                border: `1px solid ${getMatchStatusStyle(m.status).color}40`,
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                          {renderTeamRankCell(team2Entry, isTeamBasedFormat, t, openPlayerProfile, 'teams')}
                        </Box>
                      </Box>
                    </Paper>
                    );
                  })}
                </Box>
              ))}
            </Paper>
          )}

          {tabValue === 0 && isLadderFormat && (
            <Paper elevation={0} sx={{ ...featureCardStyle, mb: 3, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
                {t('tournamentPublic.ladderCourts')}
              </Typography>
              {ladder?.groups?.length ? (
                ladder.groups.map((g: any) => (
                  <Paper key={g.group_index} elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' }}>
                    <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>{g.court_label}</Typography>
                    {g.participants?.length ? (
                      <TableContainer sx={responsiveTableWrapper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={tableHeaderStyle}>#</TableCell>
                              <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.player')}</TableCell>
                              <TableCell align="right" sx={tableHeaderStyle}>{t('tournamentPublic.recordShort')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {g.participants.map((p: any, idx: number) => (
                              <TableRow key={p.participant_id}>
                                <TableCell sx={tableCellStyle}>{idx + 1}</TableCell>
                          <TableCell sx={tableCellStyle}>{renderParticipantCell(p.participant_id, p.name)}</TableCell>
                                <TableCell align="right" sx={tableCellStyle}>
                                  {t('tournamentPublic.record', {
                                    wins: p.matches_won ?? 0,
                                    losses: (p.matches_played ?? 0) - (p.matches_won ?? 0),
                                  })}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>{t('tournamentPublic.noPlayers')}</Typography>
                    )}
                  </Paper>
                ))
              ) : null}
              {ladder?.waiting_list?.length ? (
                <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' }}>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700, mb: 1 }}>{t('tournamentPublic.waitingList', 'Waiting list')}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1 }}>{t('tournamentPublic.waitingListDescription', 'No court to play in yet. Teams will move up when a slot opens.')}</Typography>
                  <TableContainer sx={responsiveTableWrapper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={tableHeaderStyle}>#</TableCell>
                          <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.player')}</TableCell>
                          <TableCell align="right" sx={tableHeaderStyle}>{t('tournamentPublic.recordShort')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ladder.waiting_list.map((p: any) => (
                          <TableRow key={p.participant_id}>
                            <TableCell sx={tableCellStyle}>{p.position}</TableCell>
                            <TableCell sx={tableCellStyle}>{renderParticipantCell(p.participant_id, p.name)}</TableCell>
                            <TableCell align="right" sx={tableCellStyle}>
                              {t('tournamentPublic.record', {
                                wins: p.matches_won ?? 0,
                                losses: (p.matches_played ?? 0) - (p.matches_won ?? 0),
                              })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : null}
              {!ladder?.groups?.length && !ladder?.waiting_list?.length ? (
                <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>{ladder ? t('tournamentPublic.noGroupsYet') : t('tournamentPublic.loadingLadder')}</Typography>
              ) : null}
            </Paper>
          )}

          {tabValue === 0 && !isBracketFormat && !isLadderFormat && (
            <Paper elevation={0} sx={{ ...featureCardStyle, mb: 3, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
                {isPointsFormat ? t('tournamentPublic.standings') : t('tournamentPublic.rankings')}
              </Typography>
              {isPointsFormat && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', mb: 2 }}>
                  {format === 'mexicano'
                    ? t('tournamentPublic.mexicanoPointsHint')
                    : t('tournamentPublic.americanoPointsHint')}
                </Typography>
              )}
              {isPointsFormat && latestRoundCourts && latestRoundCourts.courts.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'white', mb: 1.5 }}>
                    {t('tournamentPublic.roundCourts', { n: latestRoundCourts.round })}
                  </Typography>
                  <Stack spacing={2}>
                    {latestRoundCourts.courts.map(([courtLabel, courtMatches]) => (
                      <Paper
                        key={courtLabel}
                        elevation={0}
                        sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' }}
                      >
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
                          {courtLabel}
                        </Typography>
                        {courtMatches.map((m: any) => {
                          const scoreText = formatMatchScoreDisplay(m);
                          const team1Entry = matchToRowEntry(m, 'team1', participantById, tbdLabel);
                          const team2Entry = matchToRowEntry(m, 'team2', participantById, tbdLabel);
                          return (
                            <Box
                              key={m.id}
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'stretch', md: 'center' },
                                justifyContent: 'space-between',
                                gap: 1.5,
                                py: 1,
                                borderTop: '1px solid rgba(255,255,255,0.06)',
                                '&:first-of-type': { borderTop: 'none', pt: 0 },
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                {renderTeamRankCell(team1Entry, true, t, openPlayerProfile, 'teams')}
                              </Box>
                              <Box sx={{ textAlign: 'center', px: 1, flexShrink: 0 }}>
                                {scoreText ? (
                                  <Typography fontWeight="bold" sx={{ color: 'white' }}>{scoreText}</Typography>
                                ) : (
                                  <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>{vsLabel}</Typography>
                                )}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                                {renderTeamRankCell(team2Entry, true, t, openPlayerProfile, 'teams')}
                              </Box>
                            </Box>
                          );
                        })}
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
              {isFlexFormat && flexLeaderboard.length > 0 ? (
                <TableContainer sx={responsiveTableWrapper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeaderStyle}>#</TableCell>
                        <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.player')}</TableCell>
                        <TableCell align="right" sx={tableHeaderStyle}>{t('tournamentPublic.points')}</TableCell>
                        <TableCell align="right" sx={{ ...tableHeaderStyle, ...compactTableCell }}>{t('tournamentPublic.matchesPlayed')}</TableCell>
                        <TableCell align="right" sx={{ ...tableHeaderStyle, ...compactTableCell }}>{t('tournamentPublic.winPct')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {flexLeaderboard.map((entry: any, idx: number) => (
                        <TableRow key={entry.participant_id || idx}>
                          <TableCell sx={tableCellStyle}>{idx + 1}</TableCell>
                          <TableCell sx={tableCellStyle}>{renderFlexPlayerCell(entry)}</TableCell>
                          <TableCell sx={tableCellStyle} align="right">{entry.points}</TableCell>
                          <TableCell sx={{ ...tableCellStyle, ...compactTableCell }} align="right">{entry.matches_played}</TableCell>
                          <TableCell sx={{ ...tableCellStyle, ...compactTableCell }} align="right">{entry.win_percentage?.toFixed(1)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : ranking.length > 0 ? (
                <TableContainer sx={responsiveTableWrapper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeaderStyle}>#</TableCell>
                        <TableCell sx={tableHeaderStyle}>
                          {t(isTeamBasedFormat ? 'tournamentPublic.teams' : 'tournamentPublic.teamPlayer')}
                        </TableCell>
                        {isPointsFormat && (
                          <TableCell align="right" sx={tableHeaderStyle}>{t('tournamentPublic.points')}</TableCell>
                        )}
                        <TableCell align="right" sx={tableHeaderStyle}>{t('tournamentPublic.winsShort')}</TableCell>
                        <TableCell align="right" sx={{ ...tableHeaderStyle, ...compactTableCell }}>{t('tournamentPublic.lossesShort')}</TableCell>
                        <TableCell align="right" sx={{ ...tableHeaderStyle, ...compactTableCell }}>{t('tournamentPublic.winPct')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ranking.map((r: any, idx: number) => (
                        <TableRow key={r.participant_id || idx}>
                          <TableCell sx={tableCellStyle}>{idx + 1}</TableCell>
                          <TableCell sx={tableCellStyle}>{renderTeamRankCell(enrichRankingRow(r), isTeamBasedFormat, t, openPlayerProfile)}</TableCell>
                          {isPointsFormat && (
                            <TableCell sx={tableCellStyle} align="right">{r.points ?? 0}</TableCell>
                          )}
                          <TableCell sx={tableCellStyle} align="right">{r.wins}</TableCell>
                          <TableCell sx={{ ...tableCellStyle, ...compactTableCell }} align="right">{r.losses}</TableCell>
                          <TableCell sx={{ ...tableCellStyle, ...compactTableCell }} align="right">{r.win_percentage?.toFixed(1) ?? '-'}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>{t('tournamentPublic.noRankingsYet')}</Typography>
              )}
            </Paper>
          )}

          {tabValue === 1 && (
            <Paper elevation={0} sx={{ ...featureCardStyle, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
                <People sx={{ verticalAlign: 'middle', mr: 1 }} />
                {t(isTeamBasedFormat ? 'tournamentPublic.teamsTab' : 'tournamentPublic.playersTab')}
              </Typography>
              {approvedParticipants.length > 0 ? (
                <Stack spacing={1.5}>
                  {approvedParticipants.map((p) => (
                    <Paper
                      key={p.id}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        bgcolor: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.05)',
                        borderRadius: '1rem',
                      }}
                    >
                      {renderTeamRankCell(participantToRowEntry(p), isTeamBasedFormat, t, openPlayerProfile, 'teams')}
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {t(isTeamBasedFormat ? 'tournamentPublic.noTeams' : 'tournamentPublic.noPlayers')}
                </Typography>
              )}
            </Paper>
          )}

          {tabValue === 2 && (
            <Paper elevation={0} sx={{ ...featureCardStyle, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
                <SportsTennis sx={{ verticalAlign: 'middle', mr: 1 }} />
                {t('tournamentPublic.matches')}
              </Typography>
              {matches.length > 0 ? (
                matchesByRound ? (
                  <Stack spacing={3}>
                    {matchesByRound.map(([round, roundMatches]) => (
                      <Box key={round}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                          {t('tournamentPublic.roundLabel', { n: round, defaultValue: 'Round {{n}}' })}
                        </Typography>
                        {isMobile ? (
                          <Stack spacing={1.5}>
                            {roundMatches.map((m: any) => renderMobileMatchCard(m))}
                          </Stack>
                        ) : (
                        <TableContainer sx={responsiveTableWrapper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.date')}</TableCell>
                                {isPointsFormat && (
                                  <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.court')}</TableCell>
                                )}
                                <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.team1')}</TableCell>
                                <TableCell align="center" sx={tableHeaderStyle}>{t('tournamentPublic.score')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.team2')}</TableCell>
                                <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.status')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {roundMatches.map((m: any) => (
                                <TableRow key={m.id}>
                                  <TableCell sx={tableCellStyle}>{m.match_date ? new Date(m.match_date).toLocaleDateString(dateLocale) : '-'}</TableCell>
                                  {isPointsFormat && (
                                    <TableCell sx={tableCellStyle}>
                                      {m.court_name || (m.court_id ? `${t('tournamentPublic.court')} ${m.court_id}` : '-')}
                                    </TableCell>
                                  )}
                                  <TableCell sx={{ ...tableCellStyle, minWidth: { md: 180 } }}>
                                    {renderTeamRankCell(matchToRowEntry(m, 'team1', participantById, tbdLabel), showMatchAsPairs, t, openPlayerProfile, 'teams')}
                                  </TableCell>
                                  <TableCell sx={{ ...tableCellStyle, whiteSpace: 'nowrap' }} align="center">
                                    {formatMatchScoreDisplay(m) ?? '-'}
                                  </TableCell>
                                  <TableCell sx={{ ...tableCellStyle, minWidth: { md: 180 } }}>
                                    {renderTeamRankCell(matchToRowEntry(m, 'team2', participantById, tbdLabel), showMatchAsPairs, t, openPlayerProfile, 'teams')}
                                  </TableCell>
                                  <TableCell sx={tableCellStyle}>
                                    <Chip
                                      label={getTranslatedMatchStatus(m.status || 'pending', t)}
                                      size="small"
                                      sx={{
                                        bgcolor: getMatchStatusStyle(m.status).bg,
                                        color: getMatchStatusStyle(m.status).color,
                                        border: `1px solid ${getMatchStatusStyle(m.status).color}40`,
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        )}
                      </Box>
                    ))}
                  </Stack>
                ) : isMobile ? (
                <Stack spacing={1.5}>
                  {matches.map((m: any) => renderMobileMatchCard(m))}
                </Stack>
                ) : (
                <TableContainer sx={responsiveTableWrapper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.date')}</TableCell>
                        <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.team1')}</TableCell>
                        <TableCell align="center" sx={tableHeaderStyle}>{t('tournamentPublic.score')}</TableCell>
                        <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.team2')}</TableCell>
                        <TableCell sx={tableHeaderStyle}>{t('tournamentPublic.status')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matches.map((m: any) => (
                        <TableRow key={m.id}>
                          <TableCell sx={tableCellStyle}>{m.match_date ? new Date(m.match_date).toLocaleDateString(dateLocale) : '-'}</TableCell>
                          <TableCell sx={{ ...tableCellStyle, minWidth: { md: 180 } }}>
                            {renderTeamRankCell(
                              matchToRowEntry(m, 'team1', participantById, tbdLabel),
                              isTeamBasedFormat,
                              t,
                              openPlayerProfile,
                              'teams'
                            )}
                          </TableCell>
                          <TableCell sx={{ ...tableCellStyle, whiteSpace: 'nowrap' }} align="center">
                            {formatMatchScoreDisplay(m) ?? '-'}
                          </TableCell>
                          <TableCell sx={{ ...tableCellStyle, minWidth: { md: 180 } }}>
                            {renderTeamRankCell(
                              matchToRowEntry(m, 'team2', participantById, tbdLabel),
                              isTeamBasedFormat,
                              t,
                              openPlayerProfile,
                              'teams'
                            )}
                          </TableCell>
                          <TableCell sx={tableCellStyle}>
                            <Chip
                              label={getTranslatedMatchStatus(m.status || 'pending', t)}
                              size="small"
                              sx={{
                                bgcolor: getMatchStatusStyle(m.status).bg,
                                color: getMatchStatusStyle(m.status).color,
                                border: `1px solid ${getMatchStatusStyle(m.status).color}40`,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                )
              ) : (
                <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>{t('tournamentPublic.noMatchesYet')}</Typography>
              )}
            </Paper>
          )}
        </Box>
      </Box>

      {/* Footer – same as LandingPage */}
      <Box
        component="footer"
        sx={{
          width: '100%',
          py: { xs: 4, sm: 6 },
          mt: 'auto',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: 4,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: { xs: 'center', sm: 'flex-start' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img src="/PadPal.png" alt="PadPal Logo" style={{ height: 32, width: 'auto' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.02em', color: 'white' }}>
                  PadPal
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                © {new Date().getFullYear()} PadPal. Tous droits réservés.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: { xs: 'center', sm: 'flex-end' } }}>
              <Stack spacing={1.5} sx={{ alignItems: { xs: 'center', sm: 'flex-end' } }}>
                <MuiLink
                  href="mailto:contact@padpal.fr"
                  underline="hover"
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem', '&:hover': { color: 'white' }, transition: 'color 0.2s' }}
                >
                  <Email sx={{ fontSize: 16 }} />
                  contact@padpal.fr
                </MuiLink>
                <MuiLink
                  component="button"
                  onClick={() => navigate('/privacy')}
                  underline="hover"
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'flex', alignItems: 'center', gap: 1, textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', p: 0, fontSize: '0.875rem', '&:hover': { color: 'white' }, transition: 'color 0.2s' }}
                >
                  <Security sx={{ fontSize: 16 }} />
                  Politique de confidentialité
                </MuiLink>
                <MuiLink
                  component="button"
                  onClick={() => navigate('/changelog')}
                  underline="hover"
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'flex', alignItems: 'center', gap: 1, textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', p: 0, fontSize: '0.875rem', '&:hover': { color: 'white' }, transition: 'color 0.2s' }}
                >
                  <Star sx={{ fontSize: 16 }} />
                  Journal des modifications
                </MuiLink>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

      <PlayerProfileModal
        open={!!profileTarget}
        target={profileTarget}
        onClose={closePlayerProfile}
      />
    </Box>
  );
};
